import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { subscribeLeadMagnet } from '../services/api';
import { 
  Download, 
  CheckCircle2, 
  FileText, 
  ShieldCheck, 
  Award, 
  Globe,
  Lock,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const chapters = [
  {
    number: '01',
    title: 'A progresszív túlterhelés elve',
    subtitle: 'Miért nem fejlődsz hónapok óta?'
  },
  {
    number: '02',
    title: 'Agy-izom kapcsolat',
    subtitle: 'Miért a fejedben dől el a mozdulat?'
  },
  {
    number: '03',
    title: 'A megfelelő mozgástartomány (ROM)',
    subtitle: 'A kontrollált technika ereje.'
  },
  {
    number: '04',
    title: 'Regeneráció és alvás fontossága',
    subtitle: 'Hogyan nő az izom a munka mellett.'
  },
  {
    number: '05',
    title: 'A táplálkozás matematikája',
    subtitle: 'A kalóriamérleg és a fehérjebevitel.'
  }
];

const trustBadges = [
  { icon: Award, label: 'EKKR-MKKR 5. szint' },
  { icon: Globe, label: 'Europass minősítés' },
  { icon: ShieldCheck, label: 'Tudományosan megalapozott módszertan' }
];

const LeadMagnetLandingPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');

  // Always start at the top — prevents the mobile "open in middle of page" issue.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await subscribeLeadMagnet({ email, name: '' });
      if (result.success) {
        setIsSubscribed(true);
        setDownloadUrl(result.download_url);
        toast.success('Sikeres feliratkozás! A letöltési link megjelent.');
      }
    } catch (error) {
      toast.error('A feliratkozás sikertelen. Kérlek próbáld újra.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      data-testid="lead-magnet-landing-page"
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-yellow-50/30 flex flex-col"
    >
      {/* Subtle pattern background */}
      <div className="fixed inset-0 opacity-[0.025] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, #1f2937 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }}></div>

      {/* Minimal brand bar */}
      <header className="relative z-10 container mx-auto px-6 py-6">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            data-testid="landing-brand-link"
            className="text-xl md:text-2xl font-bold tracking-tight" 
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <span className="text-gray-900">OROSZ</span>
            <span className="text-yellow-600 ml-1">COACHING</span>
          </Link>
          <Link 
            to="/" 
            data-testid="landing-back-link"
            className="hidden sm:flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={16} />
            Vissza a főoldalra
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-grow container mx-auto px-6 py-8 md:py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 max-w-7xl mx-auto">
          
          {/* Left: Content */}
          <div>
            <div className="inline-block mb-5 px-4 py-2 bg-yellow-100 rounded-full">
              <span className="text-yellow-700 font-semibold text-sm tracking-wider">INGYENES SZAKMAI ANYAG</span>
            </div>

            <h1 
              data-testid="landing-headline"
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-[1.1]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Indítsd el a változást
              <span className="text-yellow-600"> tudományos alapokon</span>
              <span className="block mt-2">– teljesen ingyen!</span>
            </h1>

            <p 
              data-testid="landing-subheadline"
              className="text-lg md:text-xl text-gray-700 mb-10 leading-relaxed"
            >
              Kerüld el a stagnálást és a sérüléseket a legújabb kutatásokra és ezen 
              bizonyítékokon alapuló edzés módszerekkel.
            </p>

            {/* Chapters */}
            <div data-testid="landing-chapters">
              <div className="mb-5">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 tracking-widest uppercase">
                  <FileText size={14} />
                  Mit találsz az anyagban?
                </div>
              </div>

              <ol className="space-y-4">
                {chapters.map((chapter, index) => (
                  <li 
                    key={index}
                    data-testid={`landing-chapter-${index}`}
                    className="flex gap-5 group"
                  >
                    <div 
                      className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg bg-white border-2 border-gray-200 group-hover:border-yellow-600 group-hover:bg-yellow-50 transition-all duration-300"
                    >
                      <span className="text-lg font-bold text-yellow-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {chapter.number}
                      </span>
                    </div>
                    <div className="pt-1">
                      <div className="font-bold text-gray-900 text-base md:text-lg leading-tight">
                        {chapter.title}
                      </div>
                      <div className="text-sm md:text-base text-gray-600 mt-1">
                        {chapter.subtitle}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Trust badges */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <div className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-4">
                Szakmai háttér
              </div>
              <div className="flex flex-wrap gap-3">
                {trustBadges.map((badge, index) => {
                  const Icon = badge.icon;
                  return (
                    <div 
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700"
                    >
                      <Icon className="text-yellow-600" size={16} />
                      <span className="font-medium">{badge.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Sticky form card */}
          <div className="lg:sticky lg:top-12 lg:self-start">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-100">
              {isSubscribed ? (
                <div className="text-center py-8" data-testid="landing-success">
                  <CheckCircle2 className="mx-auto text-green-500 mb-4" size={72} />
                  <h2 
                    className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" 
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Köszönöm!
                  </h2>
                  <p className="text-gray-700 mb-2">
                    Az útmutatód készen áll a letöltésre.
                  </p>
                  <p className="text-sm text-gray-500 mb-8">
                    A linket az <span className="font-semibold">{email}</span> címre is elküldjük.
                  </p>
                  <a
                    data-testid="landing-download-btn"
                    href={downloadUrl}
                    download="5_Tudomanyosan_Igazolt_Edzesi_Alapelv.pdf"
                    className="inline-flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg px-10 py-5 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    <Download size={22} />
                    PDF letöltése
                  </a>
                </div>
              ) : (
                <>
                  {/* Resource preview */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-xl p-6 mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-600/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold tracking-widest uppercase mb-3 relative">
                      <FileText size={14} />
                      Letölthető PDF
                    </div>
                    <h2 
                      className="text-xl md:text-2xl font-bold leading-tight relative" 
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      5 tudományosan igazolt edzési alapelv az 
                      <span className="text-yellow-400"> eredményes fizikumépítéshez</span>
                    </h2>
                  </div>

                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-4" 
                    data-testid="landing-form"
                  >
                    <div>
                      <label htmlFor="landing-email" className="sr-only">E-mail cím</label>
                      <Input
                        data-testid="landing-email-input"
                        id="landing-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Add meg az e-mail címed..."
                        className="h-14 text-base px-4"
                      />
                    </div>

                    <Button
                      data-testid="landing-submit-btn"
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg py-7 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      {isSubmitting ? 'Küldés…' : 'Kérem az ingyenes PDF-et'}
                      <Download className="ml-2" size={20} />
                    </Button>
                  </form>

                  <div className="mt-5 flex items-start gap-2 text-xs text-gray-500">
                    <Lock size={14} className="flex-shrink-0 mt-0.5" />
                    <p>
                      Az adataidat soha nem osztjuk meg senkivel. Bármikor leiratkozhatsz.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Mini-disclaimer below the card */}
            {!isSubscribed && (
              <p className="text-center text-sm text-gray-500 mt-6 px-4">
                Csatlakozz a hírlevelemhez, és kapd meg azonnal a szakmai anyagot.
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="relative z-10 container mx-auto px-6 py-8 border-t border-gray-200 mt-12">
        <div className="text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Orosz Coaching · Minden jog fenntartva
        </div>
      </footer>
    </div>
  );
};

export default LeadMagnetLandingPage;
